import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { useIOSPortrait } from "@/hooks/useIOSPortrait";

import avatar01 from "@/assets/testimonials/avatar-01.jpg";
import avatar02 from "@/assets/testimonials/avatar-02.jpg";
import avatar03 from "@/assets/testimonials/avatar-03.jpg";
import avatar04 from "@/assets/testimonials/avatar-04.jpg";
import avatar05 from "@/assets/testimonials/avatar-05.jpg";
import avatar06 from "@/assets/testimonials/avatar-06.jpg";
import avatar07 from "@/assets/testimonials/avatar-07.jpg";
import avatar08 from "@/assets/testimonials/avatar-08.jpg";
import avatar09 from "@/assets/testimonials/avatar-09.jpg";
import avatar10 from "@/assets/testimonials/avatar-10.jpg";
import avatar11 from "@/assets/testimonials/avatar-11.jpg";
import avatar12 from "@/assets/testimonials/avatar-12.jpg";

const reviews = [
  // Column 1
  {
    id: 1,
    text: "This product transformed my skin routine completely. My skin has never felt better and the results are visible within weeks.",
    name: "Adaeze O.",
    title: "Verified Buyer",
    avatar: avatar01,
  },
  {
    id: 2,
    text: "Amazing quality and fast delivery. The customer service team was incredibly helpful with product recommendations.",
    name: "Chiamaka E.",
    title: "Loyal Customer",
    avatar: avatar02,
  },
  {
    id: 3,
    text: "I've tried many skincare brands, but this one stands out. The ingredients are top-notch and my skin loves it.",
    name: "Blessing A.",
    title: "Skincare Enthusiast",
    avatar: avatar03,
  },
  // Column 2
  {
    id: 4,
    text: "Finally found products that work for my sensitive skin! No irritation and visible improvement in just two weeks.",
    name: "Ngozi M.",
    title: "Verified Buyer",
    avatar: avatar04,
  },
  {
    id: 5,
    text: "The personalized skincare consultation changed everything for me. They understood my skin concerns perfectly.",
    name: "Funke T.",
    title: "Regular Customer",
    avatar: avatar05,
  },
  {
    id: 6,
    text: "My hyperpigmentation has significantly faded. These products are gentle yet effective. Highly recommend!",
    name: "Amara K.",
    title: "Beauty Blogger",
    avatar: avatar06,
  },
  // Column 3
  {
    id: 7,
    text: "Best skincare investment I've ever made. My skin is glowing and I get compliments all the time now.",
    name: "Chidinma U.",
    title: "Verified Buyer",
    avatar: avatar07,
  },
  {
    id: 8,
    text: "The serum is absolutely divine! It absorbs quickly and leaves my skin feeling hydrated all day long.",
    name: "Yetunde B.",
    title: "Skincare Lover",
    avatar: avatar08,
  },
  {
    id: 9,
    text: "Professional guidance, authentic products, and real results. This is the skincare brand I trust completely.",
    name: "Ifeoma S.",
    title: "Loyal Customer",
    avatar: avatar09,
  },
  // Extra reviews for better scrolling
  {
    id: 10,
    text: "The moisturizer is lightweight yet so nourishing. Perfect for our Nigerian climate. My skin stays balanced.",
    name: "Nneka D.",
    title: "Verified Buyer",
    avatar: avatar10,
  },
  {
    id: 11,
    text: "After years of struggling with acne, I finally found products that actually work without drying out my skin.",
    name: "Oluchi P.",
    title: "Happy Customer",
    avatar: avatar11,
  },
  {
    id: 12,
    text: "The sunscreen doesn't leave a white cast on my dark skin. Finally, a brand that understands melanin-rich skin!",
    name: "Zainab H.",
    title: "Beauty Expert",
    avatar: avatar12,
  },
] as const;

// Split reviews into 3 columns
const column1 = reviews.filter((_, i) => i % 3 === 0);
const column2 = reviews.filter((_, i) => i % 3 === 1);
const column3 = reviews.filter((_, i) => i % 3 === 2);

type Review = (typeof reviews)[number];

interface ReviewCardProps {
  review: Review;
}

function ReviewCard({ review }: ReviewCardProps) {
  return (
    <div className="group bg-foreground/90 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-lg hover:bg-foreground transition-colors duration-300 cursor-default">
      <p className="text-background/90 text-sm sm:text-base leading-relaxed mb-3 sm:mb-4">
        "{review.text}"
      </p>
      <div className="flex items-center gap-2 sm:gap-3">
        <img
          src={review.avatar}
          alt={review.name}
          width={40}
          height={40}
          loading="lazy"
          decoding="async"
          className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-primary/30"
        />
        <div>
          <p className="font-semibold text-background text-xs sm:text-sm">{review.name}</p>
          <p className="text-background/60 text-[10px] sm:text-xs">{review.title}</p>
        </div>
      </div>
    </div>
  );
}

interface ScrollingColumnProps {
  reviews: readonly Review[];
  duration: number;
  direction?: "up" | "down";
}

// Static scrollable column for iOS portrait - no Framer Motion animations
function StaticScrollingColumn({ reviews }: { reviews: readonly Review[] }) {
  return (
    <div className="relative h-[350px] sm:h-[450px] md:h-[600px] overflow-hidden overflow-y-auto">
      <div className="flex flex-col gap-3 sm:gap-4">
        {reviews.map((review, index) => (
          <ReviewCard key={`${review.id}-${index}`} review={review} />
        ))}
      </div>
      {/* Gradient overlays for fade effect */}
      <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-card to-transparent pointer-events-none z-10" />
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-card to-transparent pointer-events-none z-10" />
    </div>
  );
}

function ScrollingColumn({ reviews, duration, direction = "up" }: ScrollingColumnProps) {
  const [isPaused, setIsPaused] = useState(false);
  // Duplicate reviews for seamless loop
  const duplicatedReviews = [...reviews, ...reviews, ...reviews, ...reviews];

  return (
    <div
      className="relative h-[350px] sm:h-[450px] md:h-[600px] overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <motion.div
        className="flex flex-col gap-3 sm:gap-4"
        animate={{
          y: direction === "up" ? ["0%", "-50%"] : ["-50%", "0%"],
        }}
        transition={{
          y: {
            duration,
            repeat: Infinity,
            ease: "linear",
          },
        }}
        style={{
          animationPlayState: isPaused ? "paused" : "running",
        }}
        whileHover={{ scale: 1 }}
        onHoverStart={() => setIsPaused(true)}
        onHoverEnd={() => setIsPaused(false)}
      >
        {duplicatedReviews.map((review, index) => (
          <ReviewCard key={`${review.id}-${index}`} review={review} />
        ))}
      </motion.div>

      {/* Gradient overlays for fade effect */}
      <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-card to-transparent pointer-events-none z-10" />
      <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-card to-transparent pointer-events-none z-10" />
    </div>
  );
}

export function Testimonials() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const isIOSPortrait = useIOSPortrait();

  // iOS Portrait: Render completely static version to prevent Framer Motion transforms
  if (isIOSPortrait) {
    return (
      <section ref={ref} className="section-padding bg-card overflow-hidden">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header - Static */}
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary mb-4">
              Testimonials
            </span>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6">
              What Our <span className="text-gradient">Customers Say</span>
            </h2>
            <p className="text-muted-foreground text-lg">
              See what our customers have to say about their skincare journey with us
            </p>
          </div>

          {/* Static Scrolling Columns */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-4 md:gap-6">
            <div className="block">
              <StaticScrollingColumn reviews={column1} />
            </div>
            <div className="hidden sm:block">
              <StaticScrollingColumn reviews={column2} />
            </div>
            <div className="hidden lg:block">
              <StaticScrollingColumn reviews={column3} />
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section ref={ref} className="section-padding bg-card overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-2xl mx-auto mb-12"
        >
          <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary mb-4">
            Testimonials
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-foreground mb-6">
            What Our <span className="text-gradient">Customers Say</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            See what our customers have to say about their skincare journey with us
          </p>
        </motion.div>

        {/* Scrolling Columns */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-4 md:gap-6"
        >
          {/* Column 1 - Slower */}
          <div className="block">
            <ScrollingColumn reviews={column1} duration={35} direction="up" />
          </div>

          {/* Column 2 - Medium (hidden on smallest mobile) */}
          <div className="hidden sm:block">
            <ScrollingColumn reviews={column2} duration={28} direction="down" />
          </div>

          {/* Column 3 - Faster (only on large screens) */}
          <div className="hidden lg:block">
            <ScrollingColumn reviews={column3} duration={24} direction="up" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}
